<script lang="ts">
  import { db } from '../../lib/firebase';
  import { collection, query, orderBy, onSnapshot, where, type Query } from 'firebase/firestore';
  import { onMount } from 'svelte';
  import { FEATURE_REQUEST_STATUSES, FEATURE_REQUEST_CATEGORIES, type FeatureRequest, type FeatureRequestStatus, type FeatureRequestCategory } from '../../lib/types';
  import FeatureRequestCard from './FeatureRequestCard.svelte';

  type Props = {
    onSelectRequest: (request: FeatureRequest) => void;
  };

  let { onSelectRequest }: Props = $props();

  let requests = $state<FeatureRequest[]>([]);
  let loading = $state(true);
  let error = $state('');

  // Filters
  type SortOption = 'votes' | 'newest' | 'oldest';
  let sortBy = $state<SortOption>('votes');
  let filterStatus = $state<FeatureRequestStatus | 'all'>('all');
  let filterCategory = $state<FeatureRequestCategory | 'all'>('all');

  onMount(() => {
    const requestsRef = collection(db, 'featureRequests');
    const q = query(requestsRef, orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q,
      (snapshot) => {
        requests = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate?.() || doc.data().createdAt,
          updatedAt: doc.data().updatedAt?.toDate?.() || doc.data().updatedAt,
        })) as FeatureRequest[];
        loading = false;
      },
      (err) => {
        console.error('Error loading feature requests:', err);
        error = 'Failed to load feature requests';
        loading = false;
      }
    );

    return () => unsubscribe();
  });

  // Filtered and sorted requests
  let filteredRequests = $derived.by(() => {
    let result = [...requests];

    // Filter by status
    if (filterStatus !== 'all') {
      result = result.filter(r => r.status === filterStatus);
    }

    // Filter by category
    if (filterCategory !== 'all') {
      result = result.filter(r => r.category === filterCategory);
    }

    // Sort
    if (sortBy === 'votes') {
      result.sort((a, b) => b.voteCount - a.voteCount);
    } else if (sortBy === 'newest') {
      result.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    } else if (sortBy === 'oldest') {
      result.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    }

    return result;
  });
</script>

<div class="list-container">
  <!-- Filters -->
  <div class="filters">
    <div class="filter-group">
      <label for="sort">Sort by</label>
      <select id="sort" bind:value={sortBy}>
        <option value="votes">Most Voted</option>
        <option value="newest">Newest</option>
        <option value="oldest">Oldest</option>
      </select>
    </div>

    <div class="filter-group">
      <label for="status">Status</label>
      <select id="status" bind:value={filterStatus}>
        <option value="all">All Statuses</option>
        {#each FEATURE_REQUEST_STATUSES as status}
          <option value={status.id}>{status.label}</option>
        {/each}
      </select>
    </div>

    <div class="filter-group">
      <label for="category">Category</label>
      <select id="category" bind:value={filterCategory}>
        <option value="all">All Categories</option>
        {#each FEATURE_REQUEST_CATEGORIES as cat}
          <option value={cat.id}>{cat.label}</option>
        {/each}
      </select>
    </div>
  </div>

  <!-- Request List -->
  {#if loading}
    <div class="loading">
      <div class="spinner"></div>
      <p>Loading feature requests...</p>
    </div>
  {:else if error}
    <div class="error">{error}</div>
  {:else if filteredRequests.length === 0}
    <div class="empty">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
        <path stroke-linecap="round" stroke-linejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
      </svg>
      <p>No feature requests found</p>
      <span>Be the first to submit a feature idea!</span>
    </div>
  {:else}
    <div class="requests-list">
      {#each filteredRequests as request (request.id)}
        <FeatureRequestCard {request} onSelect={onSelectRequest} />
      {/each}
    </div>
  {/if}
</div>

<style>
  .list-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .filters {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    padding: 1rem;
    background: #f9fafb;
    border-radius: 8px;
  }

  .filter-group {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .filter-group label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .filter-group select {
    padding: 0.5rem 0.75rem;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    font-size: 0.875rem;
    background: white;
    cursor: pointer;
    min-width: 140px;
  }

  .filter-group select:focus {
    outline: none;
    border-color: #667eea;
  }

  .loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    color: #666;
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top-color: #667eea;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .loading p {
    margin-top: 1rem;
    font-size: 0.9rem;
  }

  .error {
    padding: 1rem;
    background: #fee;
    border: 1px solid #fcc;
    color: #c33;
    border-radius: 6px;
  }

  .empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    color: #999;
    text-align: center;
  }

  .empty svg {
    width: 48px;
    height: 48px;
    margin-bottom: 1rem;
    opacity: 0.5;
  }

  .empty p {
    font-size: 1rem;
    font-weight: 500;
    color: #666;
    margin: 0;
  }

  .empty span {
    font-size: 0.875rem;
    margin-top: 0.25rem;
  }

  .requests-list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  @media (max-width: 640px) {
    .filters {
      flex-direction: column;
    }

    .filter-group select {
      width: 100%;
    }
  }
</style>
